/**
 * Sysadmin Settings → IdP defaults (Phase 5 PR-C v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.9:
 *   Platform-wide OIDC/SAML/Google/Entra defaults. Per-tenant SSO config
 *   lives on the tenant admin side; this is the platform fallback.
 *
 * Built on PR-0 canonical primitives:
 *   - <SectionCard>        — groups defaults by protocol
 *   - <SettingField>       — label + description + lock indicator
 *   - <StatTile>           — 4-up summary strip
 *   - <Dialog>             — add / edit
 *   - <TypedConfirmDialog> — DELETE typed gate
 *   - <PageError>          — fetch-error surface
 *
 * Data flow:
 *   - List via useSysadminIdpDefaults (auto-poll 10s).
 *   - Group by protocol in TS (small data set; cheaper than 4 queries).
 *   - Mutations via PR-B's createSysadminMutation factory; the hooks
 *     invalidate the list query on success so refetch fires immediately.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  KeyRound,
  ShieldCheck,
  Plus,
  Edit2,
  Trash2,
  Mail,
  Globe,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatTile } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSysadminIdpDefaults,
  useCreateIdpDefault,
  useUpdateIdpDefault,
  useDeleteIdpDefault,
  type IdpDefaultRow,
  type IdpProtocol,
} from '@/lib/use-sysadmin-settings';

export const Route = createFileRoute('/sysadmin/settings/idp')({
  component: IdpDefaultsPage,
});

// ─── Constants ───────────────────────────────────────────────────────────

const PROTOCOLS: IdpProtocol[] = ['oidc', 'saml', 'google', 'entra'];

const PROTOCOL_LABELS: Record<IdpProtocol, string> = {
  oidc: 'OIDC',
  saml: 'SAML 2.0',
  google: 'Google',
  entra: 'Microsoft Entra',
};

const PROTOCOL_DESCRIPTIONS: Record<IdpProtocol, string> = {
  oidc: 'OpenID Connect discovery URL, scopes, claim mappings.',
  saml: 'SAML 2.0 metadata URL, sign+encrypt config.',
  google: 'Google Workspace OAuth client.',
  entra: 'Microsoft Entra (Azure AD) tenant.',
};

const PROTOCOL_ICONS: Record<IdpProtocol, typeof KeyRound> = {
  oidc: ShieldCheck,
  saml: Lock,
  google: Mail,
  entra: Globe,
};

// ─── Page ────────────────────────────────────────────────────────────────

function IdpDefaultsPage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminIdpDefaults();
  const [editingRow, setEditingRow] = useState<IdpDefaultRow | null>(null);
  const [addingProtocol, setAddingProtocol] = useState<IdpProtocol | null>(null);
  const [deletingRow, setDeletingRow] = useState<IdpDefaultRow | null>(null);

  const grouped = useMemo(() => {
    const byProtocol: Record<IdpProtocol, IdpDefaultRow[]> = {
      oidc: [],
      saml: [],
      google: [],
      entra: [],
    };
    for (const row of data?.items ?? []) {
      byProtocol[row.protocol].push(row);
    }
    return byProtocol;
  }, [data]);

  const counts = useMemo(() => {
    const items = data?.items ?? [];
    return {
      total: items.length,
      enabled: items.filter((i) => i.isEnabled).length,
      oidc: items.filter((i) => i.protocol === 'oidc').length,
      saml: items.filter((i) => i.protocol === 'saml').length,
    };
  }, [data]);

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load IdP defaults"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading IdP defaults…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* ── Page heading ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <KeyRound className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Identity Provider defaults
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.9</Badge>
              <Badge variant="secondary" className="text-xs">PR-C</Badge>
              <span className="text-xs text-muted-foreground">
                Platform-wide SSO defaults; tenants inherit unless overridden.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 4-up summary ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={KeyRound} tone="default" />
        <StatTile label="Enabled" value={String(counts.enabled)} icon={ShieldCheck} tone={counts.enabled > 0 ? 'success' : 'muted'} />
        <StatTile label="OIDC" value={String(counts.oidc)} icon={ShieldCheck} tone="default" />
        <StatTile label="SAML" value={String(counts.saml)} icon={Lock} tone="default" />
      </div>

      {/* ── Per-protocol sections ── */}
      {PROTOCOLS.map((protocol) => {
        const Icon = PROTOCOL_ICONS[protocol];
        const rows = grouped[protocol];
        return (
          <SectionCard
            key={protocol}
            title={PROTOCOL_LABELS[protocol]}
            icon={Icon}
            description={PROTOCOL_DESCRIPTIONS[protocol]}
            actions={
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddingProtocol(protocol)}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add default
              </Button>
            }
          >
            {rows.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No defaults configured for this protocol.
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((row) => (
                  <IdpRow
                    key={row.id}
                    row={row}
                    onEdit={() => setEditingRow(row)}
                    onDelete={() => setDeletingRow(row)}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        );
      })}

      {/* ── Add dialog ── */}
      {addingProtocol && (
        <IdpDialog
          mode="add"
          protocol={addingProtocol}
          onClose={() => setAddingProtocol(null)}
        />
      )}

      {/* ── Edit dialog ── */}
      {editingRow && (
        <IdpDialog
          mode="edit"
          row={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}

      {/* ── Delete typed-confirm ── */}
      {deletingRow && (
        <DeleteConfirm
          row={deletingRow}
          onClose={() => setDeletingRow(null)}
        />
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────

function IdpRow({
  row,
  onEdit,
  onDelete,
}: {
  row: IdpDefaultRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="text-sm font-medium truncate">{row.name}</div>
          <div className="text-[11px] text-muted-foreground font-mono truncate">
            {row.id.slice(0, 8)} · created {new Date(row.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={row.isEnabled ? 'default' : 'secondary'} className="text-[10px]">
          {row.isEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onEdit}
          title="Edit default"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onDelete}
          title="Delete default"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Add / Edit dialog ───────────────────────────────────────────────────

interface IdpDialogProps {
  mode: 'add' | 'edit';
  /** When mode='add', the protocol is fixed and `row` is undefined. */
  protocol?: IdpProtocol;
  row?: IdpDefaultRow;
  onClose: () => void;
}

function IdpDialog({ mode, protocol, row, onClose }: IdpDialogProps) {
  const activeProtocol = row?.protocol ?? protocol!;
  const [name, setName] = useState(row?.name ?? '');
  const [isEnabled, setIsEnabled] = useState(row?.isEnabled ?? false);
  const [configJson, setConfigJson] = useState(
    JSON.stringify(row?.config ?? {}, null, 2),
  );
  const [configError, setConfigError] = useState<string | null>(null);

  const createMut = useCreateIdpDefault();
  const updateMut = useUpdateIdpDefault();
  const pending = createMut.isPending || updateMut.isPending;

  const parseConfig = (): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(configJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setConfigError('Config must be a JSON object.');
        return null;
      }
      setConfigError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setConfigError(
        err instanceof Error ? err.message : 'Invalid JSON',
      );
      return null;
    }
  };

  const handleSubmit = () => {
    const config = parseConfig();
    if (!config) return;

    if (mode === 'add') {
      createMut.mutate(
        {
          protocol: activeProtocol,
          name,
          config,
          isEnabled,
        },
        { onSuccess: onClose },
      );
    } else {
      updateMut.mutate(
        {
          id: row!.id,
          name,
          config,
          isEnabled,
        },
        { onSuccess: onClose },
      );
    }
  };

  // The server returns 422 with structured details on invalid config —
  // surface that through to the user verbatim.
  const serverError =
    (createMut.error as Error | null)?.message ??
    (updateMut.error as Error | null)?.message ??
    null;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add'
              ? `Add ${PROTOCOL_LABELS[activeProtocol]} default`
              : `Edit ${row!.name}`}
          </DialogTitle>
          <DialogDescription>
            {PROTOCOL_DESCRIPTIONS[activeProtocol]} Tenants inherit this default
            unless they configure their own per-IdP override.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SettingField
            label="Display name"
            description="Shown in the sysadmin UI list. Free text; 1-200 chars."
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g. Corporate OIDC"
            />
          </SettingField>

          <SettingField
            label="Enabled"
            description="When disabled the default is stored but not used."
          >
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </SettingField>

          <SettingField
            label="Config JSON"
            description={`Provider-specific config for ${PROTOCOL_LABELS[activeProtocol]}. Validated against the protocol schema.`}
          >
            <Textarea
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              className="font-mono text-xs min-h-[180px]"
              spellCheck={false}
            />
            {configError && (
              <div className="text-xs text-destructive mt-1">{configError}</div>
            )}
          </SettingField>

          {serverError && (
            <div className="text-xs text-destructive rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
              {serverError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={pending || name.trim() === ''}
          >
            {pending ? 'Saving…' : mode === 'add' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete confirm ──────────────────────────────────────────────────────

function DeleteConfirm({
  row,
  onClose,
}: {
  row: IdpDefaultRow;
  onClose: () => void;
}) {
  const deleteMut = useDeleteIdpDefault();

  return (
    <TypedConfirmDialog
      open={true}
      onOpenChange={(o) => !o && onClose()}
      title={`Delete ${row.name}?`}
      description={
        `Soft-deletes this ${PROTOCOL_LABELS[row.protocol]} default. Tenants that inherited it will fall through to no platform default for this protocol.`
      }
      consequences={[
        'Default is marked deleted but the row remains for audit history',
        'Tenants relying on this default lose the inherited config',
        row.isEnabled
          ? 'This default is currently ENABLED — disabling it before delete is recommended'
          : 'This default is currently disabled (lower risk)',
        'A platform_audit_logs row records this deletion',
      ]}
      confirmPhrase={row.name}
      confirmLabel={deleteMut.isPending ? 'Deleting…' : 'Delete default'}
      onConfirm={() => {
        deleteMut.mutate({ id: row.id }, { onSuccess: onClose });
      }}
    />
  );
}
